import React, { useMemo, useState } from 'react';
import {
  AlertCircle,
  Calendar,
  Plus,
  RefreshCw,
  Sparkles,
  Trash2,
  Pencil,
} from 'lucide-react';
import Modal from '../../../components/ui/Modal';
import { useToast } from '../../../components/ui/Toast';
import { useConfirm } from '../../../components/ui/ConfirmDialog';
import useOtherIncome from '../hooks/useOtherIncome';
import './OtherIncomeTab.css';

const PERIODS = [
  { id: '7d',   label: '7d' },
  { id: '30d',  label: '30d' },
  { id: '90d',  label: '90d' },
  { id: 'year', label: 'Year' },
];

// Backend whitelist — mirror what /api/finance/income POST accepts.
const CATEGORIES = [
  { id: 'grant',           label: 'Grant' },
  { id: 'refund_received', label: 'Refund received' },
  { id: 'interest',        label: 'Interest' },
  { id: 'side_income',     label: 'Side income' },
  { id: 'investment',      label: 'Investment' },
  { id: 'other',           label: 'Other' },
];

const NAIRA = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  maximumFractionDigits: 0,
});
const fmtMoney = (n) => NAIRA.format(Number(n || 0));
const fmtDate  = (iso) => iso ? new Date(iso).toLocaleDateString('en-NG', {
  year: 'numeric', month: 'short', day: 'numeric',
}) : '';

const labelFor = (catId) => CATEGORIES.find((c) => c.id === catId)?.label || catId;

/* ─── Module-scope subcomponents ──────────────────────────────── */

function PeriodSwitcher({ value, onChange }) {
  return (
    <div className="fin-period-switcher" role="radiogroup" aria-label="Time period">
      {PERIODS.map((p) => (
        <button
          key={p.id}
          type="button"
          role="radio"
          aria-checked={value === p.id}
          className={`fin-period-pill${value === p.id ? ' is-active' : ''}`}
          onClick={() => onChange(p.id)}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function IncomeForm({ initial, onSubmit, onCancel, submitting }) {
  const [amount,      setAmount]      = useState(initial?.amount ? String(initial.amount) : '');
  const [category,    setCategory]    = useState(initial?.category || 'other');
  const [description, setDescription] = useState(initial?.description || '');
  const [occurredAt,  setOccurredAt]  = useState(
    initial?.occurredAt
      ? String(initial.occurredAt).slice(0, 10)
      : new Date().toISOString().slice(0, 10)
  );

  const submit = (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (!Number.isFinite(num) || num <= 0) return;
    onSubmit({
      amount: num,
      category,
      description: description.trim() || null,
      occurredAt,
    });
  };

  return (
    <form className="fin-income-form" onSubmit={submit}>
      <div className="fin-income-form-row">
        <div className="fin-income-field">
          <label htmlFor="oi-amount">Amount (₦)</label>
          <input
            id="oi-amount"
            type="number"
            step="0.01"
            min="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="0.00"
            required
            autoFocus
          />
        </div>
        <div className="fin-income-field">
          <label htmlFor="oi-category">Category</label>
          <select
            id="oi-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="fin-income-form-row">
        <div className="fin-income-field">
          <label htmlFor="oi-date">Date received</label>
          <input
            id="oi-date"
            type="date"
            value={occurredAt}
            onChange={(e) => setOccurredAt(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            required
          />
        </div>
        <div className="fin-income-field">
          <label htmlFor="oi-desc">Description <span className="fin-income-optional">(optional)</span></label>
          <input
            id="oi-desc"
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g. Q2 SME grant, Aug bank interest"
            maxLength={240}
          />
        </div>
      </div>

      <div className="fin-income-form-actions">
        <button
          type="button"
          className="fin-income-btn fin-income-btn--ghost"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="fin-income-btn fin-income-btn--primary"
          disabled={submitting}
        >
          {submitting ? 'Saving…' : (initial ? 'Save changes' : 'Log income')}
        </button>
      </div>
    </form>
  );
}

/* Card view — mobile default. Edit/Delete moved up to a top-right
   icon row so the card body is content-only (no actions footer
   eating vertical space). */
function IncomeCard({ item, onEdit, onDelete }) {
  return (
    <div className="fin-income-card">
      <div className="fin-income-card-head">
        <div className="fin-income-card-head-meta">
          <span className={`fin-income-cat fin-income-cat--${item.category}`}>
            {labelFor(item.category)}
          </span>
          <span className="fin-income-card-date">{fmtDate(item.occurredAt)}</span>
        </div>
        <div className="fin-income-card-head-tools">
          <button
            type="button"
            className="fin-income-icon-btn"
            onClick={() => onEdit(item)}
            aria-label="Edit entry"
          >
            <Pencil size={14} />
          </button>
          <button
            type="button"
            className="fin-income-icon-btn fin-income-icon-btn--danger"
            onClick={() => onDelete(item)}
            aria-label="Delete entry"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      <p className="fin-income-amount">{fmtMoney(item.amount)}</p>
      {item.description && (
        <p className="fin-income-card-desc" title={item.description}>{item.description}</p>
      )}
    </div>
  );
}

/* Table view — desktop */
function IncomeRow({ item, onEdit, onDelete }) {
  return (
    <tr>
      <td>{fmtDate(item.occurredAt)}</td>
      <td>
        <span className={`fin-income-cat fin-income-cat--${item.category}`}>
          {labelFor(item.category)}
        </span>
      </td>
      <td className="fin-income-desc-cell" title={item.description || ''}>
        {item.description || <span className="fin-income-muted">—</span>}
      </td>
      <td className="fin-income-num">{fmtMoney(item.amount)}</td>
      <td className="fin-income-actions-cell">
        <button
          type="button"
          className="fin-income-icon-btn"
          onClick={() => onEdit(item)}
          aria-label="Edit entry"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          className="fin-income-icon-btn fin-income-icon-btn--danger"
          onClick={() => onDelete(item)}
          aria-label="Delete entry"
        >
          <Trash2 size={14} />
        </button>
      </td>
    </tr>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="fin-empty">
      <Sparkles size={40} className="fin-empty-icon" />
      <h2 className="fin-empty-title">Log income that isn't from sales</h2>
      <p className="fin-empty-sub">
        Track grants, interest, refunds you received, side income — anything that adds to
        your bottom line outside of orders. Your P&L and net profit pick this up automatically.
      </p>
      <button type="button" className="fin-empty-cta" onClick={onAdd}>
        <Plus size={14} style={{ marginRight: 6 }} /> Log income
      </button>
    </div>
  );
}

function ErrorBanner({ message, onRetry }) {
  return (
    <div className="fin-error-banner" role="alert">
      <AlertCircle size={18} />
      <span>{message}</span>
      <button type="button" className="fin-error-retry" onClick={onRetry}>
        <RefreshCw size={14} /> Retry
      </button>
    </div>
  );
}

function SkeletonRows({ count = 4 }) {
  return Array.from({ length: count }).map((_, i) => (
    <div key={i} className="fin-income-card fin-income-card--skeleton">
      <div className="fin-skel-line fin-skel-line--head" />
      <div className="fin-skel-line fin-skel-line--sub" />
    </div>
  ));
}

/* ─── Main ──────────────────────────────────────────────────────── */

export default function OtherIncomeTab() {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [period, setPeriod] = useState('30d');
  const [modal, setModal] = useState({ open: false, editing: null });
  const [submitting, setSubmitting] = useState(false);

  const { items, total, loading, error, refetch, create, update, remove } =
    useOtherIncome(period);

  // Group total per category for a quick breakdown chip row
  const breakdown = useMemo(() => {
    const acc = new Map();
    for (const it of items) {
      const cur = acc.get(it.category) || 0;
      acc.set(it.category, cur + Number(it.amount || 0));
    }
    return [...acc.entries()]
      .map(([cat, amount]) => ({ category: cat, amount }))
      .sort((a, b) => b.amount - a.amount);
  }, [items]);

  const openAdd  = () => setModal({ open: true, editing: null });
  const openEdit = (item) => setModal({ open: true, editing: item });
  const closeModal = () => { if (!submitting) setModal({ open: false, editing: null }); };

  const handleSubmit = async (payload) => {
    setSubmitting(true);
    try {
      if (modal.editing) {
        await update(modal.editing.id, payload);
        toast.success('Income entry updated');
      } else {
        await create(payload);
        toast.success('Income logged');
      }
      setModal({ open: false, editing: null });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Save failed';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (item) => {
    const ok = await confirm(
      `Delete ${fmtMoney(item.amount)} ${labelFor(item.category).toLowerCase()} entry? This can't be undone.`,
      {
        title: 'Delete income entry',
        confirmText: 'Delete',
        cancelText: 'Keep',
        type: 'danger',
      }
    );
    if (!ok) return;
    try {
      await remove(item.id);
      toast.success('Entry deleted');
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Delete failed';
      toast.error(msg);
    }
  };

  const hasItems = items.length > 0;
  const showEmpty = !loading && !error && !hasItems;

  return (
    <div className="fin-income">
      <div className="fin-income-toolbar">
        <PeriodSwitcher value={period} onChange={setPeriod} />
        {hasItems && (
          <button
            type="button"
            className="fin-income-btn fin-income-btn--primary fin-income-btn--add"
            onClick={openAdd}
          >
            <Plus size={14} /> Log income
          </button>
        )}
      </div>

      {error && <ErrorBanner message={error} onRetry={refetch} />}

      {hasItems && (
        <div className="fin-income-summary">
          <div className="fin-income-summary-total">
            <p className="fin-income-summary-label">Total this period</p>
            <p className="fin-income-summary-value">{fmtMoney(total)}</p>
            <p className="fin-income-summary-meta">
              {items.length} {items.length === 1 ? 'entry' : 'entries'}
            </p>
          </div>
          {breakdown.length > 1 && (
            <div className="fin-income-summary-breakdown">
              {breakdown.slice(0, 5).map((b) => (
                <div key={b.category} className="fin-income-summary-chip">
                  <span className={`fin-income-cat-dot fin-income-cat--${b.category}`} aria-hidden />
                  <span className="fin-income-summary-chip-label">{labelFor(b.category)}</span>
                  <span className="fin-income-summary-chip-amount">{fmtMoney(b.amount)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {showEmpty && <EmptyState onAdd={openAdd} />}

      {loading && !hasItems && (
        <div className="fin-income-list">
          <SkeletonRows />
        </div>
      )}

      {hasItems && (
        <>
          {/* Mobile card list (default) */}
          <div className="fin-income-list">
            {items.map((item) => (
              <IncomeCard
                key={item.id}
                item={item}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            ))}
          </div>

          {/* Desktop table (>=1024px) */}
          <div className="fin-income-table-wrap">
            <table className="fin-income-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th className="fin-income-num">Amount</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <IncomeRow
                    key={item.id}
                    item={item}
                    onEdit={openEdit}
                    onDelete={handleDelete}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal
        isOpen={modal.open}
        onClose={closeModal}
        title={modal.editing ? 'Edit income entry' : 'Log other income'}
        size="md"
      >
        <IncomeForm
          initial={modal.editing}
          onSubmit={handleSubmit}
          onCancel={closeModal}
          submitting={submitting}
        />
      </Modal>
    </div>
  );
}
